import {INVENTORY_ITEMS} from '../../constants/inventoryItems';
import {getFormattedPrice} from '../price/getFormattedPrice';
import {ALL_CHEST_CONFIGS} from '../../configs/chests';
import {FACTION_CONFIGS} from '../../configs/factions';
import {
  ALL_FACTION_SKINS,
  getFactionSkinIconPath,
  getFactionSkinImagePath,
} from '../../configs/factions/skins/allFactionSkins';
import {getFactionIconPath} from '../factions/getFactionIconPath';
import {getFactionImagePath} from '../factions/getFactionImagePath';
import {getFactionRankIconPath} from '../factions/getFactionRankIconPath';
import {getFactionRankImagePath} from '../factions/getFactionRankImagePath';
import {checkIfRankSkinArtExists, getRankSkinLeague} from '../../configs/rankSkins';
import {getLeagueConfig} from '../../configs/leagues';
import {getFactionUnitSkinIconPath} from '../factions/getFactionUnitSkinIconPath';
import {getChestIconPath} from '../inventory/getChestIconPath';

/**
 * Отображение реферальной награды партнерки (превью в форме партнера, модалка
 * реферальной награды, история регистраций): русскоязычное название из
 * конфигурации предмета, иконка и цветовой класс.
 *
 * value: количество для нуменов/кристаллов, дни для премиума, id предмета для
 * остальных типов.
 *
 * @returns {{
 *   itemType: string|null, // тип награды (для CSS-модификаторов и подписей)
 *   title: string,        // русское название предмета (или «не найден»)
 *   amountLabel: string|null, // количество — только у числовых типов
 *   fullLabel: string,    // компактная строка (модалка/история): для валют —
 *                         // количество (иконка рядом), для предметов — title
 *   iconSrc: string|null,
 *   colorClass: string,
 *   isKnown: boolean,     // найден ли id в конфигурации (для превью формы)
 * }}
 */
export const getPartnerRewardDisplayProps = (reward) => ({
  itemType: reward?.itemType ?? null,
  ...resolveRewardDisplayProps(reward),
});

const resolveRewardDisplayProps = (reward) => {
  const {itemType, value} = reward || {};
  const stringValue = String(value ?? '').trim();

  switch (itemType) {
    case INVENTORY_ITEMS.NUMENS:
      return {
        itemType: INVENTORY_ITEMS.NUMENS,
        title: 'Нумены',
        amountLabel: getFormattedPrice(value),
        fullLabel: getFormattedPrice(value),
        iconSrc: '/img/icons/numens.png',
        colorClass: 'text-coins',
        isKnown: true,
      };
    case INVENTORY_ITEMS.CRYSTALS:
      return {
        itemType: INVENTORY_ITEMS.CRYSTALS,
        title: 'Кристаллы',
        amountLabel: getFormattedPrice(value),
        fullLabel: getFormattedPrice(value),
        iconSrc: '/img/icons/crystal.png',
        colorClass: 'text-crystals',
        isKnown: true,
      };
    case INVENTORY_ITEMS.PREMIUM:
      return {
        itemType: INVENTORY_ITEMS.PREMIUM,
        title: 'Премиум',
        amountLabel: `${value} дн.`,
        fullLabel: `Премиум на ${value} дн.`,
        iconSrc: '/img/icons/premium.png',
        colorClass: 'text-coins',
        isKnown: true,
      };
    case INVENTORY_ITEMS.CHEST: {
      const chestConfig = ALL_CHEST_CONFIGS.find((chest) => chest.id === stringValue);
      const title = chestConfig ? chestConfig.name : `Сундук «${stringValue}» не найден`;
      return {
        itemType: INVENTORY_ITEMS.CHEST,
        title,
        amountLabel: null,
        fullLabel: title,
        iconSrc: chestConfig ? getChestIconPath(stringValue) : null,
        colorClass: '',
        isKnown: Boolean(chestConfig),
      };
    }
    case INVENTORY_ITEMS.FACTION: {
      const factionConfig = FACTION_CONFIGS.find((faction) => faction.id === stringValue);
      const title = factionConfig ? `Фракция: ${factionConfig.name}` : `Фракция «${stringValue}» не найдена`;
      return {
        itemType: INVENTORY_ITEMS.FACTION,
        title,
        amountLabel: null,
        fullLabel: title,
        iconSrc: factionConfig ? getFactionIconPath(stringValue) : null,
        colorClass: '',
        isKnown: Boolean(factionConfig),
      };
    }
    case INVENTORY_ITEMS.FACTION_SKIN: {
      // Ранговые скины ({factionId}_gold / _diamond / _ruby) в конфиге скинов
      // не описаны — имя и иконка собираются из фракции и лиги. Образ без
      // арта (алмазный у нереализованной фракции) — неизвестный: его не
      // пропустит и сервер (validatePartnerRewardItemId)
      const rank = getRankSkinLeague(stringValue);
      if (rank) {
        const factionId = stringValue.slice(0, -(rank.length + 1));
        const factionConfig = checkIfRankSkinArtExists(rank, factionId)
          ? FACTION_CONFIGS.find((faction) => faction.id === factionId)
          : null;
        const title = factionConfig
          ? `Образ: ${factionConfig.name} — ${getLeagueConfig(rank).name}`
          : `Образ «${stringValue}» не найден`;
        return {
          itemType: INVENTORY_ITEMS.FACTION_SKIN,
          title,
          amountLabel: null,
          fullLabel: title,
          iconSrc: factionConfig ? getFactionRankIconPath(factionId, rank) : null,
          colorClass: '',
          isKnown: Boolean(factionConfig),
        };
      }
      const skinConfig = ALL_FACTION_SKINS.find((skin) => skin.id === stringValue);
      const factionConfig = skinConfig
        && FACTION_CONFIGS.find((faction) => faction.id === skinConfig.factionId);
      const title = skinConfig
        ? `Образ: ${skinConfig.name}${factionConfig ? ` (${factionConfig.name})` : ''}`
        : `Образ «${stringValue}» не найден`;
      return {
        itemType: INVENTORY_ITEMS.FACTION_SKIN,
        title,
        amountLabel: null,
        fullLabel: title,
        iconSrc: skinConfig ? getFactionSkinIconPath(stringValue) : null,
        colorClass: '',
        isKnown: Boolean(skinConfig),
      };
    }
    case INVENTORY_ITEMS.FACTION_UNIT_SKIN: {
      const factionConfig = FACTION_CONFIGS.find((faction) => faction.id === stringValue);
      const title = factionConfig
        ? `Образы отрядов: ${factionConfig.name}`
        : `Фракция «${stringValue}» не найдена`;
      return {
        itemType: INVENTORY_ITEMS.FACTION_UNIT_SKIN,
        title,
        amountLabel: null,
        fullLabel: title,
        iconSrc: factionConfig ? getFactionUnitSkinIconPath('unit_d20', stringValue) : null,
        colorClass: '',
        isKnown: Boolean(factionConfig),
      };
    }
    default:
      return {
        title: stringValue,
        amountLabel: null,
        fullLabel: stringValue,
        iconSrc: null,
        colorClass: '',
        isKnown: false,
      };
  }
};

/**
 * Медиа-файлы награды для кнопки «Скачать медиа» на партнерской странице:
 * картинки, которыми партнер может оформить анонс реферальной ссылки на
 * своих ресурсах. Для фракции — И полное изображение, И иконка; для скинов —
 * полное изображение образа; для остального — их основная картинка/иконка.
 * Неизвестные id пропускаются (пустой массив).
 */
export const getPartnerRewardMediaUrls = (reward) => {
  const {itemType, value} = reward || {};
  const stringValue = String(value ?? '').trim();

  switch (itemType) {
    case INVENTORY_ITEMS.NUMENS:
      return ['/img/icons/numens.png'];
    case INVENTORY_ITEMS.CRYSTALS:
      return ['/img/icons/crystal.png'];
    case INVENTORY_ITEMS.PREMIUM:
      return ['/img/icons/premium.png'];
    case INVENTORY_ITEMS.CHEST: {
      const chestConfig = ALL_CHEST_CONFIGS.find((chest) => chest.id === stringValue);
      return chestConfig ? [getChestIconPath(stringValue)] : [];
    }
    case INVENTORY_ITEMS.FACTION: {
      const factionConfig = FACTION_CONFIGS.find((faction) => faction.id === stringValue);
      return factionConfig
        ? [getFactionImagePath(stringValue), getFactionIconPath(stringValue)]
        : [];
    }
    case INVENTORY_ITEMS.FACTION_SKIN: {
      const rank = getRankSkinLeague(stringValue);
      if (rank) {
        const factionId = stringValue.slice(0, -(rank.length + 1));
        const factionConfig = checkIfRankSkinArtExists(rank, factionId)
          && FACTION_CONFIGS.find((faction) => faction.id === factionId);
        return factionConfig ? [getFactionRankImagePath(factionId, rank)] : [];
      }
      const skinConfig = ALL_FACTION_SKINS.find((skin) => skin.id === stringValue);
      return skinConfig ? [getFactionSkinImagePath(stringValue)] : [];
    }
    case INVENTORY_ITEMS.FACTION_UNIT_SKIN: {
      const factionConfig = FACTION_CONFIGS.find((faction) => faction.id === stringValue);
      return factionConfig ? [getFactionUnitSkinIconPath('unit_d20', stringValue)] : [];
    }
    default:
      return [];
  }
};
